import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

import {
  ArrowBackIos,
  ArrowForwardIos,
  EditNote,
  RemoveRedEyeOutlined,
} from '@mui/icons-material';
import {
  IBooking,
  ICoupon,
  IUser,
  IUserAddress,
} from '../../utils/entities/interface';
import { GetAdresses, GETUser, UserBookings } from '../../services/users';
import { useNavigate, useParams } from 'react-router-dom';
import CreateUserModal from '../Booking/actions/AddUserModal';
import EditAddressDialog from '../Booking/actions/EditAddress';
import { capitalizeWords, DateDDMMYYYY } from '../../utils/helper';

import { Chip } from '@mui/material';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import { GETCoupons } from '../../services/coupen';
import {
  AssignedUserCoupon,
  assignUserCoupon,
  getUserCoupons,
  removeUserCoupon,
} from '../../services/userCoupons';
import useSnackbar from '../../hooks/useSnackbar';
import { DateField } from '../Booking/Details';

const UserDetail = () => {
  const { userId } = useParams();
  const [user, setUser] = useState<IUser | null>(null);
  const [openCustomer, setOpenCustomer] = useState<boolean>(false);

  useEffect(() => {
    if (userId) {
      fetchUserDetails(userId);
    }
  }, [userId]);

  const fetchUserDetails = async (userId: string) => {
    const user = await GETUser(userId);
    setUser(user);
  };

  const reloadUser = () => {
    if (userId) {
      fetchUserDetails(userId);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#121212', minHeight: '100vh' }}>
      <DetailPageHeader title="User Detail" />

      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            backgroundColor: '#1C1C1C',
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            padding: 3,
          }}
        >
          <Typography variant="h6" color={'#FFFFFF'}>
            {user?.name && capitalizeWords(user?.name)} Detail
          </Typography>
        </Box>

        <CreateUserModal
          open={openCustomer}
          onClose={() => setOpenCustomer(false)}
          onSaved={reloadUser}
          customer={user ? user : null}
        />

        <Box
          display="flex"
          flexDirection={{ xs: 'column', lg: 'row' }}
          gap={2}
          sx={{ backgroundColor: '#1C1C1C', paddingX: 0 }}
        >
          <Card
            sx={{
              flex: 1,
              backgroundColor: '#222',
              color: 'white',
              p: 1,
              border: '1px solid #444',
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
              >
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<EditIcon />}
                  sx={{ borderRadius: 2 }}
                  onClick={() => setOpenCustomer(true)}
                >
                  Edit
                </Button>
              </Box>

              {[
                {
                  label: 'Full Name',
                  value:
                    (user && user.name && capitalizeWords(user?.name)) || 'N/A',
                },
                {
                  label: 'Email Address',
                  value: user?.email || 'N/A',
                },
                {
                  label: 'Contact Number',
                  value: user?.phoneNumber ? '+91 ' + user.phoneNumber : 'N/A',
                },
                {
                  label: 'Customer Type',
                  value:
                    user?.type && capitalizeWords(user?.type).replace('_', ' '),
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  display="flex"
                  justifyContent="flex-start"
                  alignItems="center"
                  mt={2}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#8A8D91',
                    }}
                    flex={1}
                  >
                    {item.label}
                  </Typography>

                  <Typography
                    flex={1.5}
                    sx={{
                      color: '#FFFFFF',
                    }}
                    variant="body1"
                  >
                    {item.value || 'N/A'}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          <UserAddressesComp userId={userId ? userId : ''} />
        </Box>

        <UserCouponsComp userId={userId ? userId : ''} />

        <UserBookingComp userId={userId ? userId : ''} />
      </Box>
    </Box>
  );
};

export default UserDetail;

const UserAddressesComp = ({ userId }: { userId: string }) => {
  const [userAddresses, setUserAddresses] = useState<IUserAddress[] | null>(
    null,
  );
  const [openAddress, setOpenAddress] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<IUserAddress | null>(
    null,
  );
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (userId) {
      fetchUserAddresses(userId);
    }
  }, [userId]);

  const fetchUserAddresses = async (userId: string) => {
    const addresses = await GetAdresses(userId);
    setUserAddresses(addresses.data);
  };

  const reloadAddresses = () => {
    fetchUserAddresses(userId);
  };

  const handleNext = () => {
    if (Array.isArray(userAddresses)) {
      setIndex((prev) => (prev < userAddresses.length - 1 ? prev + 1 : prev));
    }
  };

  const handlePrev = () => {
    setIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleEdit = () => {
    setSelectedAddress(userAddresses ? userAddresses[index] : null);
    setOpenAddress(true);
  };

  const getSaveAsBorderColor = (type: IUserAddress['saveAs']) => {
    switch (type) {
      case 'HOME':
        return '#4CAF50';
      case 'WORK':
        return '#2196F3';
      case 'FRIEND':
        return '#FF9800';
      case 'OTHER':
        return '#9E9E9E';
      default:
        return '#ffffff';
    }
  };

  return (
    <>
      <Card
        sx={{
          flex: 1,
          backgroundColor: '#222',
          color: 'white',
          borderRadius: 2,
          border: '1px solid #444',
        }}
      >
        <CardContent sx={{ padding: 1 }}>
          <Box
            display="flex"
            sx={{ backgroundColor: '#1C1C1C' }}
            justifyContent="space-between"
            alignItems="center"
            paddingX={2}
            paddingY={1}
            bgcolor="#1e1e1e"
            borderRadius={2}
          >
            <Typography variant="h6">User Addresses</Typography>

            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setSelectedAddress(null);
                setOpenAddress(true);
              }}
              sx={{ borderRadius: 2 }}
            >
              Add
            </Button>
          </Box>

          {Array.isArray(userAddresses) && userAddresses.length > 0 ? (
            <>
              <Box mt={2}>
                <Box
                  p={2}
                  bgcolor="#1e1e1e"
                  borderRadius={2}
                  border="1px solid #333"
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      lineHeight={1.6}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        wordBreak: 'break-word',
                        pr: { xs: 0, sm: 1 },
                      }}
                    >
                      {capitalizeWords(userAddresses[index]?.house || '')}
                      {userAddresses[index]?.house ? ', ' : ''}
                      {capitalizeWords(userAddresses[index]?.street || '')}
                    </Typography>

                    <Chip
                      label={capitalizeWords(userAddresses[index]?.saveAs || '')}
                      size="small"
                      sx={{
                        flexShrink: 0,
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        border: `1px solid ${getSaveAsBorderColor(
                          userAddresses[index]?.saveAs,
                        )}`,
                        color: getSaveAsBorderColor(userAddresses[index]?.saveAs),
                        backgroundColor: 'transparent',
                        px: '6px',
                        py: '2px',
                        height: 'auto',
                        '& .MuiChip-label': {
                          px: 0.5,
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Box>

                  {userAddresses[index]?.landmark && (
                    <Typography
                      variant="body1"
                      color="gray"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      Landmark: {capitalizeWords(userAddresses[index].landmark)}
                    </Typography>
                  )}

                  <Typography
                    variant="body1"
                    color="gray"
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {capitalizeWords(userAddresses[index]?.city || '')},{' '}
                    {capitalizeWords(userAddresses[index]?.state || '')} -{' '}
                    {userAddresses[index]?.zipcode || 'N/A'}
                  </Typography>

                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      onClick={handleEdit}
                      sx={{
                        color: '#E31E25',
                        borderRadius: '50%',
                        p: '8px',
                        transition: 'all 0.3s ease',
                      }}
                      size="medium"
                      aria-label="Edit address"
                    >
                      <EditNote fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={'center'}
                mt={2}
              >
                <IconButton
                  onClick={handlePrev}
                  disabled={index === 0}
                  sx={{ color: 'white' }}
                >
                  <ArrowBackIos />
                </IconButton>

                <Typography variant="body2">
                  {index + 1} / {userAddresses.length}
                </Typography>

                <IconButton
                  onClick={handleNext}
                  disabled={index === userAddresses.length - 1}
                  sx={{ color: 'white' }}
                >
                  <ArrowForwardIos />
                </IconButton>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="gray" mt={2}>
              No addresses found.
            </Typography>
          )}
        </CardContent>
      </Card>

      <EditAddressDialog
        open={openAddress}
        onClose={() => setOpenAddress(false)}
        exisitingAddress={selectedAddress}
        onSave={reloadAddresses}
        userId={userId}
      />
    </>
  );
};

const UserCouponsComp = ({ userId }: { userId: string }) => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const [userCoupons, setUserCoupons] = useState<AssignedUserCoupon[]>([]);
  const [availableCoupons, setAvailableCoupons] = useState<ICoupon[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState('');
  const [assignedAmount, setAssignedAmount] = useState<number | ''>('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserCoupons();
      fetchCoupons();
    }
  }, [userId]);

  const fetchUserCoupons = async () => {
    try {
      const response = await getUserCoupons(userId);
      setUserCoupons(response.data || []);
    } catch {
      setUserCoupons([]);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await GETCoupons({ page: 1, limit: 100 });
      const now = new Date();
      setAvailableCoupons(
        (response.data || []).filter(
          (coupon) => coupon.isActive && new Date(coupon.expiryDate) >= now,
        ),
      );
    } catch {
      setAvailableCoupons([]);
    }
  };

  const closeDialog = () => {
    setOpen(false);
    setSelectedCouponId('');
    setAssignedAmount('');
    setExpiresAt('');
    setNote('');
  };

  const handleAssign = async () => {
    if (!selectedCouponId || !assignedAmount) {
      showSnackbar('Select a coupon and enter amount', 'error');
      return;
    }

    setLoading(true);
    try {
      await assignUserCoupon({
        userId,
        couponId: selectedCouponId,
        assignedAmount: Number(assignedAmount),
        expiresAt: expiresAt || undefined,
        note,
      });
      showSnackbar('Coupon assigned successfully', 'success');
      closeDialog();
      fetchUserCoupons();
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to assign coupon',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (userCouponId: string) => {
    setLoading(true);
    try {
      await removeUserCoupon(userCouponId);
      showSnackbar('Coupon removed successfully', 'success');
      fetchUserCoupons();
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Failed to remove coupon',
        'error',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {SnackbarComponent}
      <Card
        sx={{
          backgroundColor: '#222',
          color: 'white',
          borderRadius: 2,
          border: '1px solid #444',
          my: 2,
        }}
      >
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Assigned Coupons</Typography>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Assign Coupon
            </Button>
          </Box>

          {userCoupons.length > 0 ? (
            userCoupons.map((coupon) => (
              <Box
                key={coupon._id}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
                sx={{
                  borderTop: '1px solid #333',
                  py: 1.5,
                }}
              >
                <Box>
                  <Typography color="#FFFFFF">
                    {coupon.couponCode} - {coupon.coupon?.discount || 0}% off
                  </Typography>
                  <Typography variant="body2" color="#8A8D91">
                    Assigned ₹{Number(coupon.assignedAmount || 0).toFixed(2)} ·
                    Used ₹{Number(coupon.usedAmount || 0).toFixed(2)} · Left ₹
                    {Number(coupon.remainingAmount || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="#8A8D91">
                    Status: {coupon.status}
                    {coupon.expiresAt
                      ? ` · Expires ${DateDDMMYYYY(String(coupon.expiresAt))}`
                      : ''}
                  </Typography>
                  {coupon.note && (
                    <Typography variant="body2" color="#8A8D91">
                      Note: {coupon.note}
                    </Typography>
                  )}
                </Box>
                {coupon.status === 'ACTIVE' ? (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={loading}
                    onClick={() => handleRemove(coupon._id)}
                  >
                    Remove
                  </Button>
                ) : null}
              </Box>
            ))
          ) : (
            <Typography color="#8A8D91">No coupons assigned yet.</Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: '#181818', color: '#FFFFFF' }}>
          Assign Coupon
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#181818', pt: 2 }}>
          <TextField
            select
            fullWidth
            label="Coupon"
            value={selectedCouponId}
            onChange={(e) => setSelectedCouponId(e.target.value)}
            sx={{ mt: 1, backgroundColor: '#222', borderRadius: 1 }}
          >
            {availableCoupons.map((coupon) => (
              <MenuItem key={coupon._id} value={coupon._id}>
                {coupon.couponCode} - {coupon.discount}% off
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Assigned Amount"
            type="number"
            value={assignedAmount}
            onChange={(e) =>
              setAssignedAmount(
                e.target.value === '' ? '' : Number(e.target.value),
              )
            }
            inputProps={{ min: 0, step: 0.01 }}
            onWheel={(e) => (e.target as HTMLElement).blur()}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
              }
            }}
            sx={{
              mt: 2,
              backgroundColor: '#222',
              borderRadius: 1,
              '& input[type=number]': { MozAppearance: 'textfield' },
              '& input[type=number]::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '& input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            }}
          />
          <DateField
            fullWidth
            label="Expiry Date"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2, backgroundColor: '#222', borderRadius: 1 }}
          />
          <TextField
            fullWidth
            label="Note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mt: 2, backgroundColor: '#222', borderRadius: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#181818' }}>
          <Button onClick={closeDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleAssign}
            disabled={loading}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const UserBookingComp = ({ userId }: { userId: string }) => {
  const [history, setHistory] = useState<IBooking[] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      FetchUserBookings(userId);
    }
  }, [userId]);

  const FetchUserBookings = async (userId: string) => {
    const bookings = await UserBookings(userId);
    setHistory(bookings);
  };

  const cellStyle: React.CSSProperties = {
    padding: '8px 0',
    fontSize: 15,
    color: '#8A8D91',
    textAlign: 'center',
    border: 'none',
  };

  return (
    <div
      style={{
        background: '#181818',
        borderEndStartRadius: 18,
        borderEndEndRadius: 18,
        color: '#F6F6F6',
        fontFamily: 'Inter, Arial, sans-serif',
        boxShadow: '0 2px 24px #00000044',
        paddingTop: 10,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          marginBottom: 2,
          letterSpacing: 0.2,
          padding: '20px 14px 14px 14px',
          color: '#FFFFFF',
        }}
      >
        Booking History
      </Typography>

      <div style={{ height: 10 }} />

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr
            style={{
              background: '#232323',
              color: '#D6D6D6',
              fontSize: 13,
              fontWeight: 500,
              textAlign: 'center',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          >
            <th style={{ padding: '10px 0', borderTopLeftRadius: 8 }}>
              Booking ID
            </th>
            <th style={{ padding: '10px 0' }}>Technician</th>
            <th style={{ padding: '10px 0' }}>Service</th>
            <th style={{ padding: '10px 0' }}>Booking Slot</th>
            <th style={{ padding: '10px 0', borderTopRightRadius: 8 }}>View</th>
          </tr>
        </thead>

        <tbody>
          {Array.isArray(history) && history.length > 0 ? (
            history.map((row, i) => (
              <React.Fragment key={row?._id || i}>
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      height: 8,
                      background: 'transparent',
                      padding: 0,
                      border: 'none',
                    }}
                  ></td>
                </tr>

                <tr
                  style={{
                    background: '#181818',
                    borderTop: '1px solid #232323',
                    borderBottom: '1px solid #232323',
                  }}
                >
                  <td style={cellStyle}>{row.bookingId || 'N/A'}</td>

                  <td style={cellStyle}>
                    {row?.technicianName?.name || 'N/A'}
                  </td>

                  <td style={cellStyle}>{row?.service?.name || 'N/A'}</td>

                  <td style={cellStyle}>
                    {(row?.date ? DateDDMMYYYY(row.date) : 'N/A') +
                      ' - ' +
                      (row.slot === 'FIRST_HALF'
                        ? '1st'
                        : row.slot === 'SECOND_HALF'
                          ? '2nd'
                          : 'N/A')}
                  </td>

                  <td style={{ textAlign: 'center' }}>
                    <IconButton
                      sx={{ color: '#E31E25', textAlign: 'center' }}
                      onClick={() => navigate(`/customers/history/${row._id}`)}
                    >
                      <RemoveRedEyeOutlined />
                    </IconButton>
                  </td>
                </tr>
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td
                colSpan={6}
                style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#888',
                  fontSize: '14px',
                  background: '#181818',
                  borderTop: '1px solid #232323',
                }}
              >
                No booking history found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ height: 12 }} />
    </div>
  );
};
